import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import router from 'plugins/router';
import { Button, Stack } from '@mui/material';

import { useSyncKoObservable } from '@/hooks/useSyncKoObservable';
import { Instance as userService } from '@/legacy/services/user';
import { durandalRoutes } from '@/routes/durandal';

import { phoneVerificationService } from '../services/phoneVerification';

import { EnterPhoneNumber } from './steps/EnterPhoneNumber';
import { EnterVerificationCode } from './steps/EnterVerificationCode';
import { VerifyNumberSuccess } from './steps/VerifyNumberSuccess';

export const VerifyNumber = (): React.JSX.Element => {
  const userProfile = useSyncKoObservable(userService.profile);
  const isCompleted = useSyncKoObservable(userProfile.isSignupPhoneVerificationDone);
  const [step, setStep] = useState<'add' | 'success' | 'verify'>(isCompleted ? 'success' : 'add');
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const onCompleteEnterPhoneNumber = useCallback((addedPhoneNumber: string) => {
    setStep('verify');
    setPhoneNumber(addedPhoneNumber);
  }, []);
  const totalAccountingFirmMemberships = useSyncKoObservable(userService.accountingFirmMemberships);
  const totalUserMemberships = useSyncKoObservable(userService.memberships);
  const onCompleteEnterVerificationCode = useCallback(async () => {
    const totalAccountingFirmMembershipsCount = totalAccountingFirmMemberships?.length ?? 0;
    const totalUserMembershipsCount = totalUserMemberships?.length ?? 0;
    const totalMembershipsCount = totalAccountingFirmMembershipsCount + totalUserMembershipsCount;
    try {
      await userService.LoadProfile(); // Reload profile to update phoneVerification status.
    } catch (_err) {
      // ignore error
    }
    if (totalMembershipsCount === 0) {
      router.navigate(durandalRoutes.user.tutorials.companyInfo);
    } else {
      router.navigate(durandalRoutes.user.dashboard);
    }
  }, [totalAccountingFirmMemberships, totalUserMemberships]);
  const onSendSms = useCallback(
    async (addedPhoneNumber: string): Promise<void> =>
      phoneVerificationService.sendCode(addedPhoneNumber),
    []
  );
  const onResendSms = useCallback(
    async (): Promise<void> => phoneVerificationService.sendCode(phoneNumber),
    [phoneNumber]
  );
  const handleLogOut = useCallback(() => {
    userService.Logout();
  }, []);
  return (
    <Stack alignItems="center" spacing={4}>
      {step === 'add' ? (
        <EnterPhoneNumber onComplete={onCompleteEnterPhoneNumber} onSendSms={onSendSms} />
      ) : null}
      {step === 'verify' ? (
        <EnterVerificationCode
          onComplete={onCompleteEnterVerificationCode}
          onResendCode={onResendSms}
          phoneNumber={phoneNumber}
        />
      ) : null}
      {step === 'success' ? <VerifyNumberSuccess /> : null}
      <Button onClick={handleLogOut} size="small" sx={{ maxWidth: 160 }} variant="text">
        <FormattedMessage defaultMessage="Sign Out" />
      </Button>
    </Stack>
  );
};
